import copy from 'copy-to-clipboard';
import React from 'react';

import { Box, IconButton, Tooltip, TooltipProps, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Check } from '@mui/icons-material';
import { useL10n } from '@uplift/hooks';
import { CopyIcon } from '@uplift/icons';

import { useToggle } from '../hooks';

const useTooltipClasses = makeStyles()(theme => ({
  tooltip: {
    marginTop: theme.spacing(1),
  },
  copyText: {
    fontSize: '14px',
    fontWeight: 450
  }
}));


export type ClickToCopyProps = {
  /**
   * The text to be copied to the clipboard
   */
  text: string;

  /**
   * Content to be shown in place of just a copy icon. Must be a `React.ReactElement` due to limitations of the MUI
   * `<Tooltip />`
   */
  children?: React.ReactElement;

  /**
   * Where to display the confirmation tooltip
   */
  placement?: TooltipProps['placement'];

  /**
   * Whether to display an arrow on the confirmation tooltip
   */
  arrow?: boolean;

  /**
   * The message to display when ready to copy
   */
  copyMessage?: string;

  /**
   * The message to display when the text has been successfully copied
   */
  copiedMessage?: string;

  /**
   * Whether to display an icon for copying
   */
  copyIcon?: boolean;

  /**
   * Whether to display an icon for successfully copying
   */
  checkIcon?: boolean;

  /**
   * The test id for the component
   */
  testId?: string;

  /**
   * The button size
   */
  size?: 'small' | 'medium';
};


export const ClickToCopy: React.VFC<ClickToCopyProps> = ({
  text,
  placement,
  children,
  arrow,
  copyIcon,
  checkIcon,
  testId = 'click-to-copy',
  size = 'small',
  ...props
}) => {
  const l10n = useL10n();

  const [ open, setOpened, setClosed ] = useToggle();
  const [ copied, setCopied ] = React.useState(false);
  const timeoutId = React.useRef<NodeJS.Timeout>();
  const { classes } = useTooltipClasses();

  const copyMessage = (
    <Box display="flex" alignItems="center" gap="4px">
      {copyIcon && <CopyIcon fontSize="small" />}
      <Typography className={classes.copyText}>
        {props.copyMessage ?? l10n.shared.ClickToCopy.copy}
      </Typography>
    </Box>
  );

  const copiedMessage = (
    <Box display="flex" alignItems="center" gap="4px">
      {checkIcon && <Check fontSize="small" />}
      <Typography className={classes.copyText}>
        {props.copiedMessage ?? l10n.shared.ClickToCopy.copied}
      </Typography>
    </Box>
  );

  const clearClose = () => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
  };

  const handleClick = () => {
    copy(text);
    setCopied(true);
    setOpened();
    clearClose();
    timeoutId.current = setTimeout(() => {
      setClosed();
    }, 1000);
  };

  return (
    <Tooltip
      arrow={arrow}
      title={copied ? copiedMessage : copyMessage}
      classes={{
        tooltip: classes.tooltip
      }}
      placement={placement}
      open={open}
      onOpen={() => {
        clearClose();
        setOpened();
      }}
      onClose={setClosed}
      TransitionProps={{
        onExited: () => {
          setCopied(false);
        }
      }}
      onClick={handleClick}
    >
      {children ?? (
        <IconButton size={size} data-testid={testId} >
          <CopyIcon fontSize="small" />
        </IconButton>
      )}
    </Tooltip>
  );
};
